import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Eye, Lock, Unlock, Globe,
  Loader2, ChevronDown, ChevronUp, Search, RefreshCw,
  ShieldAlert, ShieldCheck, UserX, Clock, Activity,
  Monitor, KeyRound, LogOut, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = '/api';

const PERIOD_OPTIONS = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '7d', value: 168 },
];

function KpiCard({ label, value, icon: Icon, color = 'text-slate-700', alert }) {
  return (
    <Card className={`border-slate-200 ${alert ? 'border-l-4 border-l-red-500' : ''}`} data-testid={`security-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
          </div>
          <div className={`p-2 rounded-lg ${alert ? 'bg-red-50' : 'bg-slate-50'}`}>
            <Icon className={`h-5 w-5 ${alert ? 'text-red-500' : 'text-slate-400'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SecurityDashboard() {
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [allowlist, setAllowlist] = useState({ enabled: false, ips: [] });
  const [sessions, setSessions] = useState([]);
  const [passwordStatus, setPasswordStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [logFilter, setLogFilter] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [showPasswordHealth, setShowPasswordHealth] = useState(false);
  const [newIp, setNewIp] = useState('');
  const navigate = useNavigate();

  useEffect(() => { loadAll(); }, [hours]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sumRes, logRes, alertRes, alRes, sessRes, pwRes] = await Promise.all([
        axios.get(`${API}/security/summary?hours=${hours}`, { withCredentials: true }),
        axios.get(`${API}/security/logs?hours=${hours}&limit=50`, { withCredentials: true }),
        axios.get(`${API}/security/alerts?hours=${hours}&resolved=false&limit=50`, { withCredentials: true }),
        axios.get(`${API}/security/ip-allowlist`, { withCredentials: true }),
        axios.get(`${API}/security/sessions`, { withCredentials: true }),
        axios.get(`${API}/security/password-status`, { withCredentials: true }),
      ]);
      setSummary(sumRes.data);
      setLogs(logRes.data.logs || []);
      setAlerts(alertRes.data.alerts || []);
      setAllowlist(alRes.data);
      setSessions(sessRes.data.sessions || []);
      setPasswordStatus(pwRes.data.users || []);
    } catch (e) {
      toast.error('Failed to load security data');
    } finally { setLoading(false); }
  };

  const resolveAlerts = async (ip) => {
    try {
      await axios.post(`${API}/security/alerts/resolve`, ip ? { ip } : { resolve_all: true }, { withCredentials: true });
      toast.success(ip ? `Resolved alerts for ${ip}` : 'All alerts resolved');
      loadAll();
    } catch (e) { toast.error('Failed to resolve'); }
  };

  const unlockAccount = async (identifier) => {
    try {
      await axios.post(`${API}/security/unlock-account`, { identifier }, { withCredentials: true });
      toast.success(`Unlocked: ${identifier}`);
      loadAll();
    } catch (e) { toast.error('Failed to unlock'); }
  };

  const toggleAllowlist = async () => {
    try {
      await axios.post(`${API}/security/ip-allowlist`, { enabled: !allowlist.enabled, ips: allowlist.ips }, { withCredentials: true });
      toast.success(allowlist.enabled ? 'IP allowlist disabled' : 'IP allowlist enabled');
      loadAll();
    } catch (e) { toast.error('Failed to update'); }
  };

  const addIp = async () => {
    if (!newIp.trim()) return;
    const updated = [...allowlist.ips, newIp.trim()];
    try {
      await axios.post(`${API}/security/ip-allowlist`, { enabled: allowlist.enabled, ips: updated }, { withCredentials: true });
      setNewIp('');
      toast.success('IP added');
      loadAll();
    } catch (e) { toast.error('Failed to add IP'); }
  };

  const removeIp = async (ip) => {
    const updated = allowlist.ips.filter(i => i !== ip);
    try {
      await axios.post(`${API}/security/ip-allowlist`, { enabled: allowlist.enabled, ips: updated }, { withCredentials: true });
      toast.success('IP removed');
      loadAll();
    } catch (e) { toast.error('Failed'); }
  };

  const terminateSession = async (userId) => {
    try {
      await axios.post(`${API}/security/sessions/terminate`, { user_id: userId }, { withCredentials: true });
      toast.success('Session terminated');
      loadAll();
    } catch (e) { toast.error('Failed to terminate session'); }
  };

  const filteredLogs = logFilter
    ? logs.filter(l => l.ip?.includes(logFilter) || l.path?.includes(logFilter) || l.flags?.some(f => f.includes(logFilter)))
    : logs;

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="h-6 w-6 animate-spin text-red-600" />
      <span className="ml-2 text-slate-500">Loading security data...</span>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-4 md:p-6" data-testid="security-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-red-600" /> Security Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Monitor threats, manage access, protect your data</p>
        </div>
        <div className="flex items-center gap-2">
          {PERIOD_OPTIONS.map(opt => (
            <Button key={opt.value} size="sm" variant={hours === opt.value ? 'default' : 'outline'} onClick={() => setHours(opt.value)} data-testid={`period-${opt.value}h`}>
              {opt.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={loadAll}><RefreshCw className="h-3 w-3" /></Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Unique IPs" value={summary?.unique_ips || 0} icon={Globe} />
        <KpiCard label="Total Requests" value={summary?.total_requests || 0} icon={Activity} />
        <KpiCard label="Auth Failures" value={summary?.total_auth_failures || 0} icon={UserX} color="text-red-600" alert={summary?.total_auth_failures > 0} />
        <KpiCard label="Suspicious" value={summary?.total_suspicious_requests || 0} icon={ShieldAlert} color="text-amber-600" alert={summary?.total_suspicious_requests > 5} />
        <KpiCard label="Locked Accounts" value={summary?.locked_accounts || 0} icon={Lock} color="text-orange-600" alert={summary?.locked_accounts > 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top IPs */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="top-ips">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" /> Active IPs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(summary?.top_ips || []).map((ip, i) => (
                <div key={ip.ip} className={`flex items-center justify-between p-2 rounded-lg ${ip.auth_failures > 0 ? 'bg-red-50 border border-red-100' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 w-5">{i + 1}</span>
                    <code className="text-sm font-mono text-slate-700">{ip.ip}</code>
                    {ip.auth_failures > 0 && (
                      <Badge className="bg-red-100 text-red-700 text-[10px]">{ip.auth_failures} auth failures</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span>{ip.requests} req</span>
                    <span>{ip.last_seen?.split('T')[1]?.slice(0, 5)}</span>
                  </div>
                </div>
              ))}
              {(summary?.top_ips || []).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No traffic recorded yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* IP Allowlist */}
        <Card className="border-slate-200" data-testid="ip-allowlist">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" /> IP Allowlist
              </CardTitle>
              <Button size="sm" variant={allowlist.enabled ? 'default' : 'outline'} onClick={toggleAllowlist} data-testid="toggle-allowlist">
                {allowlist.enabled ? <><Lock className="h-3 w-3 mr-1" /> Enabled</> : <><Unlock className="h-3 w-3 mr-1" /> Disabled</>}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-400">
              {allowlist.enabled ? 'Only these IPs can access the app.' : 'Allowlist is disabled. All IPs allowed.'}
            </p>
            <div className="flex gap-2">
              <Input placeholder="Add IP (e.g. 203.0.113.0)" value={newIp} onChange={e => setNewIp(e.target.value)} className="flex-1" data-testid="add-ip-input" />
              <Button size="sm" onClick={addIp} data-testid="add-ip-btn">Add</Button>
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {allowlist.ips.map(ip => (
                <div key={ip} className="flex items-center justify-between p-1.5 bg-slate-50 rounded text-sm">
                  <code className="text-slate-600">{ip}</code>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 h-6 px-1" onClick={() => removeIp(ip)}>Remove</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Alerts */}
      <Card className={`border-slate-200 ${alerts.length > 0 ? 'border-l-4 border-l-amber-500' : ''}`} data-testid="security-alerts">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Active Alerts ({alerts.length})
            </CardTitle>
            {alerts.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => resolveAlerts(null)} data-testid="resolve-all-btn">
                <ShieldCheck className="h-3 w-3 mr-1" /> Resolve All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-6 text-slate-400">
              <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm">No active security alerts</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {alerts.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2 flex-1">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="flex gap-1 flex-wrap">
                        {(a.flags || []).map(f => (
                          <Badge key={f} className="bg-amber-100 text-amber-800 text-[9px]">{f}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        <code>{a.ip}</code> &rarr; {a.path} ({a.status_code})
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-400">{a.timestamp?.split('T')[1]?.slice(0, 8)}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => resolveAlerts(a.ip)}>
                      Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card className="border-slate-200" data-testid="active-sessions">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 cursor-pointer" onClick={() => setShowSessions(!showSessions)}>
              <Monitor className="h-5 w-5 text-blue-600" /> Active Sessions ({sessions.length})
              {showSessions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CardTitle>
          </div>
        </CardHeader>
        {showSessions && (
          <CardContent>
            {sessions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No active sessions</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {sessions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                        <Monitor className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{s.user_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <span>{s.user_email}</span>
                          <Badge variant="outline" className="text-[9px]">{s.role}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                          <code>{s.ip || 'N/A'}</code>
                          <span>Last: {s.last_active ? s.last_active.split('T')[1]?.slice(0, 8) : 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7 px-2 text-xs shrink-0" onClick={() => terminateSession(s.user_id)} data-testid={`terminate-session-${i}`}>
                      <LogOut className="h-3 w-3 mr-1" /> End
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Password Health */}
      <Card className="border-slate-200" data-testid="password-health">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 cursor-pointer" onClick={() => setShowPasswordHealth(!showPasswordHealth)}>
              <KeyRound className="h-5 w-5 text-purple-600" /> Password Health ({passwordStatus.length} users)
              {showPasswordHealth ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CardTitle>
            {showPasswordHealth && passwordStatus.some(u => u.expired || u.must_change_password) && (
              <Badge className="bg-red-100 text-red-700 text-xs">
                <AlertCircle className="h-3 w-3 mr-1" />
                {passwordStatus.filter(u => u.expired || u.must_change_password).length} need attention
              </Badge>
            )}
          </div>
        </CardHeader>
        {showPasswordHealth && (
          <CardContent>
            <div className="text-xs text-slate-400 mb-3">
              Passwords expire after 90 days. Min 8 chars with uppercase, lowercase, digit, and special character.
            </div>
            {passwordStatus.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No password users found</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {passwordStatus.map((u, i) => {
                  const isAlert = u.expired || u.must_change_password;
                  return (
                    <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border ${isAlert ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-700">{u.name || u.email}</p>
                          <Badge variant="outline" className="text-[9px]">{u.role}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                          {u.days_since_change !== null ? (
                            <span>{u.days_since_change} days since last change</span>
                          ) : (
                            <span className="text-amber-500">Never changed</span>
                          )}
                          {u.must_change_password && <Badge className="bg-amber-100 text-amber-700 text-[9px]">Must change</Badge>}
                          {u.expired && <Badge className="bg-red-100 text-red-700 text-[9px]">Expired</Badge>}
                        </div>
                      </div>
                      {isAlert && <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Audit Logs */}
      <Card className="border-slate-200" data-testid="audit-logs">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 cursor-pointer" onClick={() => setShowLogs(!showLogs)}>
              <Eye className="h-5 w-5 text-slate-600" /> Audit Logs ({logs.length})
              {showLogs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CardTitle>
            {showLogs && (
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-slate-400" />
                <Input placeholder="Filter by IP, path, flag..." value={logFilter} onChange={e => setLogFilter(e.target.value)} className="w-48 h-8 text-xs" data-testid="log-filter" />
              </div>
            )}
          </div>
        </CardHeader>
        {showLogs && (
          <CardContent>
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-slate-400 border-b">
                    <th className="text-left py-1.5 px-2">Time</th>
                    <th className="text-left py-1.5 px-2">IP</th>
                    <th className="text-left py-1.5 px-2">Method</th>
                    <th className="text-left py-1.5 px-2">Path</th>
                    <th className="text-center py-1.5 px-2">Status</th>
                    <th className="text-left py-1.5 px-2">Flags</th>
                    <th className="text-right py-1.5 px-2">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((l, i) => (
                    <tr key={i} className={`border-b border-slate-50 ${l.flags?.length > 0 ? 'bg-amber-50' : 'hover:bg-slate-50'}`}>
                      <td className="py-1 px-2 text-slate-400 whitespace-nowrap">{l.timestamp?.split('T')[1]?.slice(0, 8)}</td>
                      <td className="py-1 px-2 font-mono text-slate-600">{l.ip}</td>
                      <td className="py-1 px-2">
                        <Badge variant="outline" className={`text-[9px] ${l.method === 'POST' ? 'text-blue-600' : 'text-slate-500'}`}>{l.method}</Badge>
                      </td>
                      <td className="py-1 px-2 text-slate-600 max-w-xs truncate">{l.path}</td>
                      <td className="py-1 px-2 text-center">
                        <Badge className={`text-[9px] ${l.status_code < 300 ? 'bg-emerald-100 text-emerald-700' : l.status_code < 400 ? 'bg-blue-100 text-blue-700' : l.status_code < 500 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {l.status_code}
                        </Badge>
                      </td>
                      <td className="py-1 px-2">
                        {(l.flags || []).map(f => (
                          <Badge key={f} className="bg-red-50 text-red-600 text-[8px] mr-0.5">{f}</Badge>
                        ))}
                      </td>
                      <td className="py-1 px-2 text-right text-slate-400">{l.elapsed_ms}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
